/**
 * Home — landing pública de Ceciluz Arte.
 *
 * Rediseño visual editorial-místico alineado con la referencia entregada por
 * el cliente (mockup 2026-05-22):
 *
 *   1. Hero violeta noche con composición ilustrada (luna creciente, loto,
 *      ramas y estrellas doradas) + título serif + 2 CTAs.
 *   2. Terapias destacadas — 4 cards crema con icono espiritual arriba.
 *   3. Productos artísticos destacados — 4 cards con imagen superior.
 *   4. Próximos eventos — 3 cards oscuras tipo "noche" con imagen mística.
 *   5. Testimonios — 3 cards crema con comilla dorada y avatar.
 *   6. Comunidad en Instagram — 6 miniaturas + CTA violeta.
 *   7. Newsletter — banda violeta noche al final, integrada al Footer.
 *
 * Reglas:
 *  - No toca backend, rutas ni componentes globales (ServiceCard/ProductCard/
 *    TestimonialCard quedan intactos; el Home inline las versiones visuales
 *    que necesita la referencia para no afectar otras páginas).
 *  - Lee datos de los hooks existentes (`useFeaturedTherapies`,
 *    `useFeaturedProducts`, `useUpcomingEvents`, `useTestimonials`,
 *    `useInstagramGallery`, `useSiteSettings`) — sin cambios en API ni RLS.
 *  - Headings y subtextos consultables vía `usePageCopy(slug, key, fallback)`
 *    para que el admin pueda editarlos desde `/admin/contenido` sin deploy.
 *  - Sólo tokens del DS. Cero hex literal.
 *
 * Responsive:
 *  - Desktop: layout completo de referencia.
 *  - Mobile: hero compacto vertical, terapias en lista vertical, productos en
 *    grid 2 cols, eventos como card principal + indicadores, testimonio
 *    único, CTA Instagram compacta.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Star } from 'lucide-react';

import { Section } from '../components/layout/Section';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Image } from '../components/ui/Image';
import { Skeleton } from '../components/ui/Skeleton';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { FormField } from '../components/ui/FormField';
import { Input } from '../components/ui/Input';
import { useToast } from '../components/ui/ToastProvider';
import { BolsaIcon, CorazonIcon, LotoIcon } from '../components/icons';

import { cn } from '../lib/cn';
import { Routes } from '../lib/routes';
import { useSEO, pageSEO } from '../lib/seo';
import { usePageCopy } from '../features/site-content/usePageCopy';
import { SpiritualSymbol, pickSymbol } from '../components/icons/SpiritualSymbol';
import {
  useSiteSettings,
  useFeaturedTherapies,
  useFeaturedProducts,
  useTestimonials,
  type FeaturedProduct,
  type HomeTestimonial,
  type SiteAbout,
  type SiteHero,
} from '../features/home/api';
import { HeroSection } from '../features/home/HeroSection';
import { TherapyMiniCard } from '../features/home/TherapyMiniCard';
import { EventsBlock } from '../features/home/EventsBlock';
import { InstagramBlock } from '../features/home/InstagramBlock';
import { SectionHeading } from '../features/home/SectionHeading';
import { StarDot, MoonCrescent } from '../features/home/ornaments';
import { clampStars, copFormatter } from '../features/home/format';
import { TestimonialForm } from '../features/testimonials/TestimonialForm';
import { useGalleryItems, type GalleryItem as GalleryListItem } from '../features/gallery/api';

// ===========================================================================
// Helpers
// ===========================================================================

// ===========================================================================
// Sub: HeroSection
// ===========================================================================

// ===========================================================================
// Sub: TherapiesBlock
// ===========================================================================

function TherapiesBlock() {
  const { data, isLoading, isError, error, refetch } = useFeaturedTherapies();
  const heading = usePageCopy('home', 'seccion_terapias.heading', 'Terapias destacadas');

  return (
    <section
      aria-labelledby="home-therapies-title"
      className="relative bg-cream-100 py-14 lg:py-20"
    >
      {/* Ornamentos laterales — sólo desktop. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none">
        <MoonCrescent className="absolute left-8 top-10 hidden h-10 w-10 text-gold-500/60 lg:block" />
        <MoonCrescent className="absolute right-8 bottom-10 hidden h-10 w-10 text-gold-500/60 lg:block" />
        <StarDot className="absolute left-[6%] top-[40%] hidden text-xs opacity-50 lg:inline" />
        <StarDot className="absolute right-[6%] top-[60%] hidden text-xs opacity-50 lg:inline" />
      </div>

      <Container>
        <SectionHeading
          id="home-therapies-title"
          eyebrow={heading.toUpperCase()}
          ctaLabel="Ver todas"
          ctaHref={Routes.therapies}
          align="split"
        />

        {isLoading && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={220} rounded="lg" />
            ))}
          </div>
        )}

        {isError && (
          <ErrorState
            title="No se pudieron cargar las terapias"
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {!isLoading && !isError && (data?.length ?? 0) === 0 && (
          <EmptyState
            illustration={<LotoIcon size={64} />}
            title="Pronto agregaremos terapias"
            description="Estamos preparando experiencias de sanación para ti."
          />
        )}

        {!isLoading && !isError && (data?.length ?? 0) > 0 && (
          <ul role="list" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(data ?? []).slice(0, 4).map((t) => (
              <li key={t.id} className="h-full">
                <TherapyMiniCard therapy={t} />
              </li>
            ))}
          </ul>
        )}
      </Container>
    </section>
  );
}

// ===========================================================================
// Sub: ProductsBlock
// ===========================================================================

function ProductMiniCard({ product }: { product: FeaturedProduct }) {
  // Rating placeholder visual — los productos no tienen aún reviews_count en
  // el modelo de datos; mostramos 5 estrellas suaves.
  return (
    <Link
      to={Routes.productDetail(product.slug)}
      aria-label={`Ver ${product.title}`}
      className={cn(
        'group flex h-full flex-col overflow-hidden rounded-lg',
        'border border-cream-200 bg-cream-50',
        'shadow-elev1 hover:shadow-elev2',
        'transition-all duration-2 ease-out',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
      )}
    >
      {/* Imagen superior con esquinas redondeadas (heredadas del card). */}
      <div className="relative aspect-square w-full overflow-hidden bg-cream-200">
        {product.cover_url ? (
          <Image
            src={product.cover_url}
            alt={product.title}
            width={400}
            height={400}
            ratio="1:1"
            className="h-full w-full object-cover transition-transform duration-3 ease-out group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-primary-700/40">
            <BolsaIcon size={64} />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <h3 className="font-display text-h4 text-primary-900 line-clamp-1">{product.title}</h3>
        <p className="font-display text-h4 text-primary-700">
          {copFormatter.format(product.price_cop)}
        </p>
        <div className="mt-1 flex items-center gap-1" aria-label="Calificación: 5 de 5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              aria-hidden="true"
              className="h-3.5 w-3.5 fill-gold-500 text-gold-500"
              strokeWidth={1.5}
            />
          ))}
        </div>
      </div>
    </Link>
  );
}

function ProductsBlock() {
  const { data, isLoading, isError, error, refetch } = useFeaturedProducts();
  const heading = usePageCopy(
    'home',
    'seccion_productos.heading',
    'Productos artísticos destacados',
  );

  return (
    <section aria-labelledby="home-products-title" className="bg-cream-50 py-14 lg:py-20">
      <Container>
        <SectionHeading
          id="home-products-title"
          eyebrow={heading.toUpperCase()}
          ctaLabel="Ver tienda completa"
          ctaHref={Routes.shop}
          align="split"
        />

        {isLoading && (
          <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height={360} rounded="lg" />
            ))}
          </div>
        )}

        {isError && (
          <ErrorState
            title="No se pudieron cargar las piezas"
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {!isLoading && !isError && (data?.length ?? 0) === 0 && (
          <EmptyState
            illustration={<BolsaIcon size={64} />}
            title="Pronto verás nuevas piezas"
            description="Estamos preparando arte hecho con intención."
          />
        )}

        {!isLoading && !isError && (data?.length ?? 0) > 0 && (
          <ul role="list" className="grid grid-cols-2 gap-5 lg:grid-cols-4">
            {(data ?? []).slice(0, 4).map((p) => (
              <li key={p.id} className="h-full">
                <ProductMiniCard product={p} />
              </li>
            ))}
          </ul>
        )}
      </Container>
    </section>
  );
}

// ===========================================================================
// Sub: GalleryBlock — "Últimas Obras"
//
// Hace descubrible el catálogo `/galeria` desde el Home (QA UI 2026-06-01).
// Grid simple de 6 thumbnails cuadrados con título sutil debajo, cada uno
// linkea al deep-link del lightbox `/galeria#<slug>` (la página de Galería
// ya tiene `useGalleryItemBySlug(hashSlug)` que abre el lightbox al cargar
// con el hash). El SectionHeading lleva CTA "Ver galería completa".
//
// Coherencia con el sistema editorial: como las cards de Galería son
// fotográficas (estilo Pinterest) y NO requieren bloque texto sólido — el
// título vive afuera como caption — mantenemos el patrón Pinterest aquí
// también, distinto del editorial-clásica usado en Productos/Eventos. Es
// el mismo patrón ya consagrado en la página `/galeria`.
// ===========================================================================

/** Acepta sólo URLs absolutas. Mismo helper que en `pages/Galeria.tsx` —
 *  reusable inline para no exportar API extra desde la página. Sirve para
 *  ignorar paths relativos de seeds (`gallery/foo.webp`) que el browser
 *  resolvería como URL relativa al dominio y devolvería 404. */
function isResolvableImageUrl(value: string | null | undefined): value is string {
  if (!value) return false;
  return /^(https?:\/\/|data:)/i.test(value);
}

function pickGalleryThumbnail(item: GalleryListItem): string | null {
  if (isResolvableImageUrl(item.cover_url)) return item.cover_url;
  const first = item.media_urls.find(
    (u) => isResolvableImageUrl(u) && !/\.(mp4|webm|mov)(\?|#|$)/i.test(u),
  );
  return first ?? null;
}

function GalleryMiniCard({ item }: { item: GalleryListItem }) {
  const thumb = pickGalleryThumbnail(item);
  const symbol = pickSymbol(item.slug, `${item.title} ${item.type}`);
  return (
    <Link
      to={Routes.galleryItem(item.slug)}
      aria-label={`Ver ${item.title} en la galería`}
      className={cn(
        'group flex h-full flex-col gap-2',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
      )}
    >
      <div
        className={cn(
          'relative aspect-square w-full overflow-hidden rounded-lg',
          'border border-cream-200 bg-cream-100',
          'shadow-elev1 transition-shadow duration-2 ease-out group-hover:shadow-elev2',
        )}
      >
        {thumb ? (
          <Image
            src={thumb}
            alt={item.title}
            width={400}
            height={400}
            ratio="1:1"
            fit="cover"
            className="h-full w-full transition-transform duration-3 ease-out group-hover:scale-105"
          />
        ) : (
          // Fallback simbólico: panel violeta + símbolo espiritual premium
          // (variante estable + coherente con el nombre/type de la obra).
          <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center bg-primary-900"
          >
            <div className="absolute -left-6 -top-6 h-24 w-24 rounded-pill bg-primary-500/25 blur-3xl" />
            <div className="absolute -right-6 -bottom-6 h-24 w-24 rounded-pill bg-gold-500/15 blur-3xl" />
            <SpiritualSymbol variant={symbol} tone="dark" size={96} />
          </div>
        )}
      </div>
      <p className="text-center font-display text-body text-primary-700 line-clamp-1">
        {item.title}
      </p>
    </Link>
  );
}

function GalleryBlock() {
  const { data, isLoading, isError, error, refetch } = useGalleryItems(undefined, 'recent');
  const heading = usePageCopy('home', 'seccion_galeria.heading', 'Últimas obras');
  const items = (data ?? []).slice(0, 6);

  return (
    <section aria-labelledby="home-gallery-title" className="bg-cream-100 py-14 lg:py-20">
      <Container>
        <SectionHeading
          id="home-gallery-title"
          eyebrow={heading.toUpperCase()}
          ctaLabel="Ver galería completa"
          ctaHref={Routes.gallery}
          align="split"
        />

        {isLoading && (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={200} rounded="lg" />
            ))}
          </div>
        )}

        {isError && (
          <ErrorState
            title="No se pudo cargar la galería"
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {!isLoading && !isError && items.length === 0 && (
          <EmptyState
            illustration={<LotoIcon size={64} />}
            title="Pronto compartiremos nuevas obras"
            description="El taller está creando piezas con intención."
          />
        )}

        {!isLoading && !isError && items.length > 0 && (
          <ul role="list" className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {items.map((item) => (
              <li key={item.id} className="h-full">
                <GalleryMiniCard item={item} />
              </li>
            ))}
          </ul>
        )}
      </Container>
    </section>
  );
}

// ===========================================================================
// Sub: TestimonialsBlock
// ===========================================================================

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

function TestimonialMiniCard({ t }: { t: HomeTestimonial }) {
  const stars = clampStars(t.rating);
  return (
    <article
      aria-label={`Testimonio de ${t.author_name}`}
      className={cn(
        'flex h-full flex-col gap-4',
        'rounded-lg border border-cream-200 bg-cream-50 p-6',
        'shadow-elev1',
      )}
    >
      {/* Comilla decorativa dorada */}
      <span aria-hidden="true" className="font-display text-[44px] leading-none text-gold-500">
        “
      </span>
      <p className="text-body italic text-primary-900/85 line-clamp-4">{t.body}</p>

      <div className="mt-auto flex items-center gap-3 border-t border-cream-200 pt-4">
        {t.avatar_url ? (
          <img src={t.avatar_url} alt="" className="h-10 w-10 rounded-pill object-cover" />
        ) : (
          <span
            aria-hidden="true"
            className="inline-flex h-10 w-10 items-center justify-center rounded-pill bg-gold-500/20 font-display text-h4 text-primary-700"
          >
            {getInitials(t.author_name)}
          </span>
        )}
        <div className="flex flex-col">
          <p className="font-display text-body text-primary-700">{t.author_name}</p>
          <div className="flex items-center gap-0.5" aria-label={`Calificación: ${stars} de 5`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                aria-hidden="true"
                className={cn(
                  'h-3.5 w-3.5',
                  i < stars ? 'fill-gold-500 text-gold-500' : 'text-cream-200',
                )}
                strokeWidth={1.5}
              />
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}

function TestimonialsBlock() {
  const { data, isLoading, isError, error, refetch } = useTestimonials();
  const heading = usePageCopy('home', 'seccion_testimonios.heading', 'Testimonios');
  const items = (data ?? []).slice(0, 3);

  return (
    <section aria-labelledby="home-testimonials-title" className="bg-cream-50 py-14 lg:py-20">
      <Container>
        <SectionHeading
          id="home-testimonials-title"
          eyebrow={heading.toUpperCase()}
          ctaLabel="Ver más testimonios"
          ctaHref={`${Routes.contact}#testimonios`}
          align="split"
        />

        {isLoading && (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} height={220} rounded="lg" />
            ))}
          </div>
        )}

        {isError && (
          <ErrorState
            title="No se pudieron cargar los testimonios"
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {!isLoading && !isError && items.length === 0 && (
          <EmptyState
            illustration={<CorazonIcon size={64} />}
            title="Aún no hay testimonios publicados"
            description="Pronto compartiremos voces de quienes han caminado este proceso."
          />
        )}

        {!isLoading && !isError && items.length > 0 && (
          <>
            {/* Desktop: 3 cards. Mobile: solo la primera (referencia). */}
            <ul role="list" className="hidden grid-cols-3 gap-5 lg:grid">
              {items.map((t) => (
                <li key={t.id} className="h-full">
                  <TestimonialMiniCard t={t} />
                </li>
              ))}
            </ul>
            <div className="lg:hidden">
              <TestimonialMiniCard t={items[0]} />
            </div>
          </>
        )}

        {/* Trigger para abrir el formulario dentro de un `<Modal>`. Antes el
            form estaba expuesto permanentemente; QA UX 2026-07-17 lo movió
            a modal para no sobrecargar la sección con dos surfaces (voces
            existentes + form activo). El formulario mismo, la mutación
            `useSubmitTestimonial` y el flujo de éxito no cambiaron. */}
        <TestimonialCta />
      </Container>
    </section>
  );
}

/**
 * CTA "Compartir mi experiencia" — botón centrado que abre el
 * `<TestimonialForm />` dentro de un `<Modal>`. La lógica de envío,
 * validación y success state vive intacta en `TestimonialForm` — este
 * componente sólo controla la apertura/cierre.
 */
function TestimonialCta() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className="mt-10 lg:mt-14 flex justify-center">
        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={() => setOpen(true)}
          leftIcon={<Sparkles aria-hidden="true" strokeWidth={1.5} />}
        >
          Compartir mi experiencia
        </Button>
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Comparte tu experiencia"
        description="Tu testimonio inspira a otras almas que buscan sanación. Cecilia lo leerá con cariño antes de publicarlo."
        size="md"
      >
        <TestimonialForm chromeless onClose={() => setOpen(false)} />
      </Modal>
    </>
  );
}

// ===========================================================================
// Sub: NewsletterBlock — banda violeta noche pre-footer
// ===========================================================================

function NewsletterBlock() {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const heading = usePageCopy('home', 'newsletter.heading', 'Recibe luz en tu bandeja');
  const description = usePageCopy(
    'home',
    'newsletter.description',
    'Novedades, eventos y mensajes para tu alma.',
  );

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    toast({
      variant: 'success',
      title: '¡Gracias por inscribirte!',
      description: 'Pronto activaremos las suscripciones al boletín.',
    });
    setEmail('');
    setSubmitting(false);
  }

  return (
    <Section background="primary-900" padding="lg" aria-labelledby="home-newsletter-title">
      <Container width="narrow">
        <div className="flex flex-col items-center gap-6 text-center">
          <h2
            id="home-newsletter-title"
            className="inline-flex items-center gap-3 font-display text-h2 text-cream-50"
          >
            {heading}
            <Sparkles className="h-6 w-6 text-gold-500" aria-hidden="true" />
          </h2>
          <p className="text-body text-cream-100/85 max-w-xl">{description}</p>

          <form
            onSubmit={handleSubmit}
            className="flex w-full max-w-xl flex-col items-stretch gap-3 sm:flex-row"
            aria-label="Suscripción al boletín"
            noValidate
          >
            <div className="flex-1 text-left">
              <FormField label="Correo electrónico" hideLabel htmlFor="home-newsletter-email">
                <Input
                  id="home-newsletter-email"
                  type="email"
                  required
                  placeholder="Tu correo electrónico"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="bg-cream-50 text-primary-900 placeholder:text-primary-900/40 border-gold-500/50 focus:border-gold-500"
                />
              </FormField>
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={submitting}
              className="bg-gold-500 text-primary-900 hover:bg-gold-300"
            >
              Suscribirme
            </Button>
          </form>
        </div>
      </Container>
    </Section>
  );
}

// ===========================================================================
// Página
// ===========================================================================

export function Home() {
  useSEO(pageSEO.home);

  const { data: settings } = useSiteSettings();
  const hero = useMemo<SiteHero | undefined>(
    () => (settings?.hero ?? undefined) as SiteHero | undefined,
    [settings],
  );

  return (
    <>
      <HeroSection hero={hero} />
      <TherapiesBlock />
      <ProductsBlock />
      <EventsBlock />
      <GalleryBlock />
      {/* Orden QA UX 2026-07-17: Testimonios pasa a ir DEBAJO de la
          comunidad en Instagram y JUSTO ANTES del boletín. La secuencia
          social (Instagram → voces reales) desemboca en el cierre por
          suscripción — cadena editorial más natural. */}
      <InstagramBlock />
      <TestimonialsBlock />
      <NewsletterBlock />
    </>
  );
}

// Re-export del tipo para descargar pruebas/pages dependientes.
export type { SiteAbout };
